import { CREDENTIAL_FIELDS } from '../models/credential-forms';
import {
  CommissionAgreement,
  CommissionLine,
  CommissionSplit,
  CompanyCredential,
  Invitation,
  LineOfBusiness,
  ProtoDb,
} from '../models/domain.models';
import { INVITATION_VALID_DAYS } from '../models/rules';
import { companyBaseRate, hashOf, holderOf, lineCompanyPays, lineExpected, monthOf, nextPeriodStart } from './commission-math';
import { addDays } from './rng';
import { COMPANY, MEMBER, MEMBER_FED } from './seed-named';

const PRODUCER_ADJUST: Record<string, number> = {
  [MEMBER.martin]: 0, [MEMBER.laura]: -2, [MEMBER.diego]: -3, [MEMBER.gestion]: -1.5,
};
const ALL_LINES: LineOfBusiness[] = ['auto', 'home', 'commerce', 'liability', 'personal-accident', 'life'];
const SUB_TOLERANCE_SHORTFALL = 300;
/** Federal Andina loads its settlements by hand; in these months back it only typed the total. */
const WITHOUT_DETAIL_MONTHS_BACK = [2, 4, 6, 8, 10, 12];
const SPECIMEN: Record<string, string> = {
  user: 'usuario.demo', password: 'clave-demo', apiKey: 'ak-demo-0001', organizerCode: 'ORG-204', token: 'tok-demo-0001',
};

function roundTo100(n: number): number {
  return Math.round(n / 100) * 100;
}

function monthsBack(period: string, today: string): number {
  const [y, m] = period.split('-').map(Number);
  const [ty, tm] = monthOf(today).split('-').map(Number);
  return ty * 12 + tm - (y * 12 + m);
}

function seedAgreements(db: ProtoDb, today: string): CommissionAgreement[] {
  const out: CommissionAgreement[] = [];
  const skip = (producer: string, company: string, line: LineOfBusiness): boolean =>
    (producer === MEMBER.gestion && (company === COMPANY.pampa || company === COMPANY.litoral))
    || (producer === MEMBER.diego && line === 'life')
    || (producer === MEMBER.laura && company === COMPANY.litoral);
  for (const producer of [MEMBER.martin, MEMBER.laura, MEMBER.diego, MEMBER.gestion]) {
    for (const company of db.companies) {
      for (const line of ALL_LINES.filter(l => company.lines.includes(l))) {
        if (skip(producer, company.oid, line)) continue;
        const rate = companyBaseRate(company.oid, line) + (PRODUCER_ADJUST[producer] ?? 0);
        const base = { producerOid: producer, companyOid: company.oid, lineOfBusiness: line };
        const key = `${producer.slice(4)}-${company.oid.slice(4)}-${line}`;
        if (producer === MEMBER.martin && company.oid === COMPANY.austral && line === 'auto') {
          out.push({ ...base, oid: `agr-${key}-1`, ratePct: rate - 2, validFrom: addDays(today, -400), validTo: addDays(today, -181) });
          out.push({ ...base, oid: `agr-${key}-2`, ratePct: rate, validFrom: addDays(today, -180), validTo: null });
        } else {
          out.push({ ...base, oid: `agr-${key}-1`, ratePct: rate, validFrom: addDays(today, -400), validTo: null });
        }
      }
    }
  }
  return out;
}

function seedSplits(today: string): CommissionSplit[] {
  const out: CommissionSplit[] = [];
  const add = (parent: string, member: string, pctByLine: Partial<Record<LineOfBusiness, number>>) => {
    for (const line of ALL_LINES) {
      const pct = pctByLine[line];
      if (pct === undefined) continue;
      const base = { parentOid: parent, memberOid: member, lineOfBusiness: line };
      const key = `${parent.slice(4)}-${member.slice(4)}-${line}`;
      if (member === MEMBER.laura && line === 'auto') {
        out.push({ ...base, oid: `spl-${key}-1`, sharePct: pct - 5, validFrom: addDays(today, -400), validTo: addDays(today, -201) });
        out.push({ ...base, oid: `spl-${key}-2`, sharePct: pct, validFrom: addDays(today, -200), validTo: null });
      } else {
        out.push({ ...base, oid: `spl-${key}-1`, sharePct: pct, validFrom: addDays(today, -400), validTo: null });
      }
    }
  };
  const uniform = (pct: number): Partial<Record<LineOfBusiness, number>> =>
    Object.fromEntries(ALL_LINES.map(l => [l, pct]));
  add(MEMBER.martin, MEMBER.laura, uniform(60));
  add(MEMBER.martin, MEMBER.gestion, uniform(65));
  add(MEMBER.martin, MEMBER.sofia, uniform(40));
  add(MEMBER.martin, MEMBER.tomas, { ...uniform(45), life: undefined });
  add(MEMBER.laura, MEMBER.diego, uniform(50));
  add(MEMBER.laura, MEMBER.valentina, uniform(35));
  return out;
}

function seedCredentials(db: ProtoDb, today: string): CompanyCredential[] {
  const out: CompanyCredential[] = [];
  const configure = (
    memberOid: string, companyOid: string, validation: CompanyCredential['validation'],
    lineFilter: (line: LineOfBusiness) => boolean = () => true, message: string | null = null,
  ) => {
    const company = db.companies.find(c => c.oid === companyOid)!;
    const member = db.members.find(m => m.oid === memberOid)!;
    out.push({
      oid: `crd-${memberOid.slice(4)}-${companyOid.slice(4)}`, memberOid, companyOid,
      fields: Object.fromEntries((CREDENTIAL_FIELDS[companyOid] ?? []).map(f => [f.key, SPECIMEN[f.key] ?? 'demo'])),
      producerCode: `${companyOid.slice(4).toUpperCase()}-${member.license?.slice(4) ?? '000'}`,
      validation, validatedDate: validation === 'unvalidated' ? null : addDays(today, -30),
      validationMessage: message, enabledLines: company.lines.filter(lineFilter),
    });
  };
  for (const company of db.companies) configure(MEMBER.martin, company.oid, 'valid');
  for (const company of db.companies.filter(c => c.oid !== COMPANY.litoral)) {
    if (company.oid === COMPANY.pampa) {
      configure(MEMBER.laura, company.oid, 'invalid', l => l !== 'commerce',
        'Credenciales inválidas: la compañía respondió "usuario bloqueado"');
    } else {
      configure(MEMBER.laura, company.oid, 'valid', l => l !== 'commerce');
    }
  }
  configure(MEMBER.diego, COMPANY.plata, 'valid');
  configure(MEMBER.diego, COMPANY.austral, 'valid');
  configure(MEMBER.diego, COMPANY.federal, 'unvalidated');
  for (const companyOid of [COMPANY.plata, COMPANY.austral, COMPANY.federal, COMPANY.nexo]) {
    configure(MEMBER.gestion, companyOid, 'valid', l => l === 'auto' || l === 'home');
  }
  return out;
}

function seedInvitations(db: ProtoDb, today: string): Invitation[] {
  const accepted = (oid: string, memberOid: string, invitedBy: string, daysAgo: number): Invitation => {
    const member = db.members.find(m => m.oid === memberOid)!;
    const sent = addDays(today, -daysAgo);
    return {
      oid, invitedByOid: invitedBy, email: member.email, license: member.license ?? '', taxId: member.taxId ?? '',
      existingPerson: false, legalForm: member.legalForm, name: member.name, sentDate: sent,
      expiryDate: addDays(sent, INVITATION_VALID_DAYS), accepted: true, memberOid,
    };
  };
  const pendingSent = addDays(today, -4);
  const expiredSent = addDays(today, -40);
  return [
    accepted('inv-0001', MEMBER.laura, MEMBER.martin, 395),
    accepted('inv-0002', MEMBER.gestion, MEMBER.martin, 380),
    accepted('inv-0003', MEMBER.diego, MEMBER.laura, 300),
    {
      oid: 'inv-0004', invitedByOid: MEMBER.martin, email: 'nicolas.arias@estudioarias.example', license: 'MAT-81234',
      taxId: '20-29887766-2', existingPerson: false, legalForm: 'natural', name: 'Nicolás Arias',
      sentDate: pendingSent, expiryDate: addDays(pendingSent, INVITATION_VALID_DAYS), accepted: false, memberOid: null,
    },
    {
      oid: 'inv-0005', invitedByOid: MEMBER.martin, email: 'hernan.dupont@brokers.example', license: 'MAT-55120',
      taxId: '20-27111222-3', existingPerson: true, legalForm: 'natural', name: 'Hernán Dupont',
      sentDate: expiredSent, expiryDate: addDays(expiredSent, INVITATION_VALID_DAYS), accepted: false, memberOid: null,
    },
  ];
}

function seedTree(db: ProtoDb, today: string): void {
  db.invitations = seedInvitations(db, today);
  for (const member of db.members) {
    const invitation = db.invitations.find(i => i.memberOid === member.oid);
    member.invitationOid = invitation?.oid ?? null;
    member.statusHistory = [{
      date: invitation ? addDays(invitation.sentDate, 2) : addDays(today, -420), status: 'active', byOid: member.parentOid,
      text: member.parentOid ? 'Alta en el árbol' : 'Raíz del árbol: contrató la plataforma',
    }];
    if (member.status === 'suspended') {
      member.statusHistory.push({
        date: addDays(today, -18), status: 'suspended', byOid: member.parentOid, text: 'Suspendido por su productor padre',
      });
    }
  }
  db.credentials = seedCredentials(db, today);
  db.agreements = seedAgreements(db, today);
  db.splits = seedSplits(today);
}

function seedLines(db: ProtoDb, today: string): void {
  const currentPeriod = monthOf(today);
  const periods = new Set([currentPeriod, ...db.settlements.map(s => s.period)]);
  const lines: CommissionLine[] = [];
  for (const installment of db.installments) {
    if (installment.status !== 'paid' || !installment.paidDate) continue;
    const period = monthOf(installment.paidDate);
    if (!periods.has(period)) continue;
    const policy = db.policies.find(p => p.oid === installment.policyOid);
    if (!policy) continue;
    const settlement = db.settlements.find(s => s.companyOid === policy.companyOid && s.period === period);
    const accrued = !settlement || (period !== currentPeriod && hashOf(installment.oid) % 23 === 0);
    lines.push({
      oid: `cln-${installment.oid.slice(4)}`, settlementOid: accrued ? null : settlement.oid,
      companyOid: policy.companyOid, period, policyOid: policy.oid, ownerOid: policy.ownerOid,
      holderOid: holderOf(db.members, policy.ownerOid), lineOfBusiness: policy.lineOfBusiness,
      installmentOid: installment.oid, installmentNumber: installment.number, paidDate: installment.paidDate,
      collectedPremium: installment.amount, settled: null, ticketOid: null,
    });
  }
  for (const line of lines) {
    if (!line.settlementOid) continue;
    const settlement = db.settlements.find(s => s.oid === line.settlementOid)!;
    const withoutDetail = settlement.companyOid === COMPANY.federal && WITHOUT_DETAIL_MONTHS_BACK.includes(monthsBack(settlement.period, today));
    line.settled = withoutDetail ? null : lineCompanyPays(db.agreements, line);
  }
  const expectedOfLine = (line: CommissionLine): number | null => lineExpected(db.agreements, line);
  const claimTickets = db.tickets.filter(t => t.kind === 'commission-claim' && t.relatedType === 'settlement');
  const claimedPolicies = new Set<string>();
  for (const ticket of claimTickets) {
    const target = lines.find(l => l.settlementOid === ticket.relatedOid && l.settled !== null && l.ticketOid === null
      && !claimedPolicies.has(l.policyOid) && (expectedOfLine(l) ?? 0) >= 4000);
    if (!target) continue;
    claimedPolicies.add(target.policyOid);
    const expected = expectedOfLine(target) ?? 0;
    target.settled = expected - roundTo100(expected * 0.35);
    target.ticketOid = ticket.oid;
    const policy = db.policies.find(p => p.oid === target.policyOid);
    ticket.title = `Reclamo de comisión ${target.period}: póliza ${policy?.number ?? ''}`;
    ticket.personOid = policy?.personOid ?? null;
    ticket.events = [{
      date: ticket.lastActivityDate,
      text: `La liquidación pagó $ ${target.settled} y el convenio indica $ ${expected} (cuota ${target.installmentNumber} de la póliza ${policy?.number ?? ''}).`,
    }];
  }
  lines.forEach((line, i) => {
    const expected = expectedOfLine(line);
    if (line.settled !== null && line.ticketOid === null && expected !== null && expected >= 3000 && i % 23 === 4) {
      line.settled = expected - SUB_TOLERANCE_SHORTFALL;
    }
  });
  db.commissionLines = lines;
}

function seedSettlements(db: ProtoDb, today: string): void {
  for (const settlement of db.settlements) {
    const company = db.companies.find(c => c.oid === settlement.companyOid)!;
    const mode = company.capabilities.settlements;
    const back = monthsBack(settlement.period, today);
    const received = `${nextPeriodStart(settlement.period).slice(0, 8)}10`;
    const lines = db.commissionLines.filter(l => l.settlementOid === settlement.oid);
    settlement.receivedDate = received > today ? today : received;
    settlement.via = mode === 'file' ? 'file' : mode === 'api' ? 'api' : 'manual';
    settlement.fileName = settlement.via === 'file' ? `liquidacion-${company.oid.slice(4)}-${settlement.period}.csv` : null;
    settlement.hasDetail = !(company.oid === COMPANY.federal && WITHOUT_DETAIL_MONTHS_BACK.includes(back));
    settlement.settledAmount = lines.reduce((sum, l) => sum + (l.settled ?? lineCompanyPays(db.agreements, l)), 0);
    settlement.unmatched = company.oid === COMPANY.nexo && back === 2 ? 2 : 0;
  }
}

/** Second tree: same mechanics with a small, uniform tree (no per-person exceptions). */
function seedFederalTree(db: ProtoDb, today: string): void {
  const producers = db.members.filter(m => m.kind === 'producer');
  const sent = (daysAgo: number) => addDays(today, -daysAgo);
  const accepted = (oid: string, memberOid: string, invitedBy: string, daysAgo: number): Invitation => {
    const member = db.members.find(m => m.oid === memberOid)!;
    return {
      oid, invitedByOid: invitedBy, email: member.email, license: member.license ?? '', taxId: member.taxId ?? '',
      existingPerson: member.oid === MEMBER_FED.martin, legalForm: member.legalForm, name: member.name,
      sentDate: sent(daysAgo), expiryDate: addDays(sent(daysAgo), INVITATION_VALID_DAYS), accepted: true, memberOid,
    };
  };
  db.invitations = [
    accepted('inv-f-0001', MEMBER_FED.martin, MEMBER_FED.ferro, 210),
    accepted('inv-f-0002', MEMBER_FED.claudia, MEMBER_FED.ferro, 160),
    {
      oid: 'inv-f-0003', invitedByOid: MEMBER_FED.ferro, email: 'lucia.bravo@brokersur.example', license: 'MAT-90112',
      taxId: '27-31456789-8', existingPerson: false, legalForm: 'natural', name: 'Lucía Bravo',
      sentDate: sent(5), expiryDate: addDays(sent(5), INVITATION_VALID_DAYS), accepted: false, memberOid: null,
    },
  ];
  for (const member of db.members) {
    const invitation = db.invitations.find(i => i.memberOid === member.oid);
    member.invitationOid = invitation?.oid ?? null;
    member.statusHistory = [{
      date: invitation ? addDays(invitation.sentDate, 2) : addDays(today, -260), status: 'active', byOid: member.parentOid,
      text: member.parentOid ? 'Alta en el árbol' : 'Raíz del árbol: contrató la plataforma',
    }];
    if (member.status === 'suspended') {
      member.statusHistory.push({
        date: sent(25), status: 'suspended', byOid: null, text: 'Suspendido por falta de pago de la suscripción',
      });
    }
  }
  db.agreements = [];
  for (const producer of producers) {
    for (const company of db.companies) {
      for (const line of ALL_LINES.filter(l => company.lines.includes(l))) {
        db.agreements.push({
          oid: `agr-${producer.oid.slice(4)}-${company.oid.slice(4)}-${line}-1`, producerOid: producer.oid,
          companyOid: company.oid, lineOfBusiness: line, ratePct: companyBaseRate(company.oid, line) - (producer.parentOid ? 1.5 : 0),
          validFrom: sent(300), validTo: null,
        });
      }
    }
  }
  db.splits = [];
  for (const member of db.members.filter(m => m.parentOid)) {
    for (const line of ALL_LINES) {
      db.splits.push({
        oid: `spl-${member.parentOid!.slice(4)}-${member.oid.slice(4)}-${line}-1`, parentOid: member.parentOid!,
        memberOid: member.oid, lineOfBusiness: line, sharePct: member.kind === 'seller' ? 40 : 55,
        validFrom: sent(300), validTo: null,
      });
    }
  }
  db.credentials = [];
  const configure = (memberOid: string, companyOid: string, validation: CompanyCredential['validation']) => {
    const company = db.companies.find(c => c.oid === companyOid)!;
    const member = db.members.find(m => m.oid === memberOid)!;
    db.credentials.push({
      oid: `crd-${memberOid.slice(4)}-${companyOid.slice(4)}`, memberOid, companyOid,
      fields: Object.fromEntries((CREDENTIAL_FIELDS[companyOid] ?? []).map(f => [f.key, SPECIMEN[f.key] ?? 'demo'])),
      producerCode: `${companyOid.slice(4).toUpperCase()}-${member.license?.slice(4) ?? '000'}`,
      validation, validatedDate: validation === 'unvalidated' ? null : sent(30), validationMessage: null,
      enabledLines: company.lines,
    });
  };
  for (const company of db.companies) {
    configure(MEMBER_FED.ferro, company.oid, 'valid');
    configure(MEMBER_FED.martin, company.oid, 'valid');
  }
  configure(MEMBER_FED.claudia, COMPANY.plata, 'valid');
  configure(MEMBER_FED.claudia, COMPANY.nexo, 'unvalidated');
}

/** Commission and tree data derived from the base seed without the RNG, so the earlier passes' data stays identical. */
export function enrichCommissions(db: ProtoDb, today: string, idp = ''): void {
  if (idp) seedFederalTree(db, today);
  else seedTree(db, today);
  seedLines(db, today);
  seedSettlements(db, today);
}
